import React, { Component } from 'react';

import '../../style/ProjectStats.scss';
import { backEndUri } from '../../config';
import { t } from '../../i18n';
import { identityProvider } from '../../utils';

const COLORS = ['#5b5ce6', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#a855f7', '#ec4899', '#84cc16'];
const PERIODS = [7, 30, 90, 0];
const nf = (n) => new Intl.NumberFormat().format(n || 0);
const pct = (part, total) => (total > 0 ? `${Math.round((part / total) * 1000) / 10} %` : '—');

// Camembert (donut) : une part par page, la couleur sert aussi de légende dans le tableau.
function Donut({ slices, total }) {
  const R = 70; const C = 2 * Math.PI * R;
  let offset = 0;
  return (
    <svg viewBox='0 0 200 200' className='tsk-pstats__donut' role='img' aria-label={t('ui.stats.share_of_clicks')}>
      <circle cx='100' cy='100' r={R} fill='none' stroke='currentColor' strokeOpacity='0.08' strokeWidth='34' />
      {total > 0 && slices.map((s) => {
        const len = (s.value / total) * C;
        const el = (
          <circle key={s.label} cx='100' cy='100' r={R} fill='none' stroke={s.color} strokeWidth='34'
            strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-offset} transform='rotate(-90 100 100)'>
            <title>{`${s.label} — ${nf(s.value)} (${pct(s.value, total)})`}</title>
          </circle>
        );
        offset += len;
        return el;
      })}
      <text x='100' y='96' textAnchor='middle' className='tsk-pstats__donut-num'>{nf(total)}</text>
      <text x='100' y='118' textAnchor='middle' className='tsk-pstats__donut-label'>{t('ui.stats.clicks')}</text>
    </svg>
  );
}

// Histogramme par jour (clics) avec les vues en arrière-plan.
function Timeline({ days }) {
  if (!days.length) return <p className='tsk-pstats__none'>{t('ui.stats.no_data')}</p>;
  const max = Math.max(1, ...days.map((d) => Math.max(d.clicks, d.views)));
  return (
    <svg viewBox='0 0 600 160' preserveAspectRatio='none' className='tsk-pstats__timeline' role='img' aria-label={t('ui.stats.per_day')}>
      {days.map((d, i) => {
        const x = (i * 600) / days.length; const bw = Math.max(2, (600 / days.length) * 0.7);
        const hv = (d.views / max) * 140; const hc = (d.clicks / max) * 140;
        return (
          <g key={d.day}>
            <rect x={x} y={150 - hv} width={bw} height={hv} className='tsk-pstats__bar-views' />
            <rect x={x} y={150 - hc} width={bw} height={hc} className='tsk-pstats__bar-clicks'>
              <title>{`${d.day} — ${nf(d.clicks)} ${t('ui.stats.clicks')}, ${nf(d.views)} ${t('ui.stats.views')}`}</title>
            </rect>
          </g>
        );
      })}
      <line x1='0' x2='600' y1='150' y2='150' stroke='currentColor' strokeOpacity='0.2' />
    </svg>
  );
}

function HBars({ rows, labelKey, valueKey, colorFn }) {
  const max = Math.max(1, ...rows.map((r) => r[valueKey]));
  return (
    <ul className='tsk-pstats__hbars'>
      {rows.map((r, i) => (
        <li key={`${r[labelKey]}-${i}`}>
          <span className='tsk-pstats__hbars-label' title={r[labelKey]}>{r[labelKey]}</span>
          <span className='tsk-pstats__hbars-track'>
            <span style={{ width: `${(r[valueKey] / max) * 100}%`, background: colorFn ? colorFn(i) : COLORS[0] }} />
          </span>
          <span className='tsk-pstats__hbars-value'>{nf(r[valueKey])}</span>
        </li>
      ))}
    </ul>
  );
}

// Page « Statistiques » d'un projet : remplace le simple téléchargement du CSV (toujours disponible en bouton).
export default class ProjectStats extends Component {
  state = { days: 30, data: null, error: false, loading: true };

  componentDidMount() { this.load(); }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.projectId !== this.props.match.params.projectId) this.load();
  }

  async load(days = this.state.days) {
    const { projectId } = this.props.match.params;
    this.setState({ days, loading: true, error: false });
    try {
      let token;
      try { token = await identityProvider.currentUser().jwt(); } catch (e) { /* sans jeton */ }
      const response = await fetch(`${backEndUri}/project/${projectId}/stats/summary?days=${days}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error(String(response.status));
      this.setState({ data: await response.json(), loading: false });
    } catch (e) {
      this.setState({ error: true, loading: false });
    }
  }

  render() {
    const { projectId } = this.props.match.params;
    const { days, data, error, loading } = this.state;
    const totals = data ? data.totals : null;
    const UNKNOWN = t('ui.stats.unknown_page');
    const pages = data ? data.pages.filter((p) => p.clicks > 0 || p.views > 0).map((p) => (p.page === '—' ? { ...p, page: UNKNOWN, unknown: true } : p)) : [];
    const clickPages = pages.filter((p) => p.clicks > 0);
    const slices = clickPages.slice(0, 7).map((p, i) => ({ label: p.page, value: p.clicks, color: COLORS[i] }));
    const rest = clickPages.slice(7).reduce((sum, p) => sum + p.clicks, 0);
    if (rest > 0) slices.push({ label: t('ui.stats.others'), value: rest, color: '#94a3b8' });
    const named = clickPages.filter((p) => !p.unknown);
    const top = named[0];
    const byRate = pages.filter((p) => !p.unknown && p.views >= 5).map((p) => ({ ...p, rate: p.clicks / p.views })).sort((a, b) => b.rate - a.rate)[0];
    const quiet = pages.filter((p) => !p.unknown && p.views >= 5).sort((a, b) => a.clicks / a.views - b.clicks / b.views)[0];

    return (
      <div className='tsk-page-board tsk-pstats'>
        <header className='tsk-page-hero'>
          <h1 className='tsk-page-hero__title'>{t('ui.stats.title')}</h1>
          <p className='tsk-page-hero__subtitle'>{t('ui.stats.sub')}</p>
        </header>

        <div className='tsk-pstats__toolbar'>
          <div className='tsk-pstats__periods'>
            {PERIODS.map((p) => (
              <button key={p} type='button' className={p === days ? 'is-active' : ''} onClick={() => this.load(p)}>
                {p === 0 ? t('ui.stats.all_time') : t('ui.stats.last_days', { n: p })}
              </button>
            ))}
          </div>
          <a className='tsk-pstats__csv' href={`${backEndUri}/project/${projectId}/stats`} download>{t('ui.stats.csv')}</a>
        </div>

        {loading && !data && <p>{t('ui.common.loading')}</p>}
        {error && <p>{t('ui.stats.error')}</p>}

        {data && (
          <div style={{ opacity: loading ? 0.5 : 1 }}>
            <div className='tsk-pstats__kpis'>
              <div><strong>{nf(totals.clicks)}</strong><span>{t('ui.stats.clicks')}</span></div>
              <div><strong>{nf(totals.views)}</strong><span>{t('ui.stats.views')}</span></div>
              <div><strong>{totals.sessions > 0 ? (Math.round((totals.clicks / totals.sessions) * 10) / 10) : '—'}</strong><span>{t('ui.stats.clicks_per_session')}</span></div>
              <div><strong>{nf(totals.sessions)}</strong><span>{t('ui.stats.sessions')}</span></div>
              <div><strong>{nf(totals.devices)}</strong><span>{t('ui.stats.screens')}</span></div>
            </div>

            {pages.length === 0 ? (
              <div className='tsk-empty'><h3>{t('ui.stats.no_data')}</h3><p>{t('ui.stats.no_data_text')}</p></div>
            ) : (
              <>
                {(top || byRate || quiet) && (
                  <ul className='tsk-pstats__insights'>
                    {top && <li>{t('ui.stats.insight_top', { page: top.page, share: pct(top.clicks, totals.clicks) })}</li>}
                    {byRate && <li>{t('ui.stats.insight_rate', { page: byRate.page, rate: pct(byRate.clicks, byRate.views) })}</li>}
                    {quiet && quiet.page !== (byRate && byRate.page) && <li>{t('ui.stats.insight_quiet', { page: quiet.page, rate: pct(quiet.clicks, quiet.views) })}</li>}
                  </ul>
                )}

                <div className='tsk-pstats__grid'>
                  <section className='tsk-pstats__card'>
                    <h2>{t('ui.stats.share_of_clicks')}</h2>
                    <div className='tsk-pstats__donut-wrap'>
                      <Donut slices={slices} total={slices.reduce((s, x) => s + x.value, 0)} />
                      <ul className='tsk-pstats__legend'>
                        {slices.map((s) => (
                          <li key={s.label}><i style={{ background: s.color }} /><span>{s.label}</span><b>{pct(s.value, totals.clicks)}</b></li>
                        ))}
                      </ul>
                    </div>
                  </section>

                  <section className='tsk-pstats__card'>
                    <h2>{t('ui.stats.compare_pages')}</h2>
                    <HBars rows={clickPages.slice(0, 10)} labelKey='page' valueKey='clicks' colorFn={(i) => COLORS[i] || '#94a3b8'} />
                  </section>

                  <section className='tsk-pstats__card tsk-pstats__card--wide'>
                    <h2>{t('ui.stats.per_day')}</h2>
                    <Timeline days={data.days} />
                    <p className='tsk-pstats__hint'><i className='tsk-pstats__sw tsk-pstats__sw--views' />{t('ui.stats.views')} <i className='tsk-pstats__sw tsk-pstats__sw--clicks' />{t('ui.stats.clicks')}</p>
                  </section>

                  <section className='tsk-pstats__card tsk-pstats__card--wide'>
                    <h2>{t('ui.stats.table')}</h2>
                    <table className='tsk-pstats__table'>
                      <thead><tr><th>{t('ui.stats.page')}</th><th>{t('ui.stats.views')}</th><th>{t('ui.stats.clicks')}</th><th>{t('ui.stats.click_rate')}</th><th>{t('ui.stats.share')}</th></tr></thead>
                      <tbody>
                        {pages.map((p) => (
                          <tr key={p.page}><td>{p.page}</td><td>{nf(p.views)}</td><td>{nf(p.clicks)}</td><td>{pct(p.clicks, p.views)}</td><td>{pct(p.clicks, totals.clicks)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </section>

                  {data.devices.length > 1 && (
                    <section className='tsk-pstats__card'>
                      <h2>{t('ui.stats.compare_screens')}</h2>
                      <HBars rows={data.devices.slice(0, 8)} labelKey='device' valueKey='clicks' />
                    </section>
                  )}

                  {data.flows.length > 0 && (
                    <section className='tsk-pstats__card'>
                      <h2>{t('ui.stats.flows')}</h2>
                      <HBars rows={data.flows.slice(0, 8).map((f) => ({ label: `${f.from_page} → ${f.to_page}`, count: f.count }))} labelKey='label' valueKey='count' />
                    </section>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  }
}
